/**
 * usePresenceTracker — montato globalmente in AppInner.
 * Traccia ogni visitatore nel canale Realtime Presence "site_visitors"
 * usando il singleton condiviso di presenceChannel.ts.
 *
 * GDPR: trasmette solo visitor_id anonimo, user_id e pagina corrente.
 * Nessun dato sensibile (no email, no nome).
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { getPresenceChannel, destroyPresenceChannel } from '@/lib/presenceChannel';
import { getOrCreateAnonymousId } from '@/utils/anonymousUserUtils';
import { useAuthContext } from '@/contexts/auth';
import { isBot } from '@/utils/botDetection';

export interface VisitorPresence {
  visitor_id: string;
  user_id: string | null;
  user_role: string | null;
  current_page: string;
  joined_at: string;
}

export function usePresenceTracker() {
  const location = useLocation();
  const { user, isAuthenticated } = useAuthContext();
  const visitorIdRef = useRef<string | null>(null);

  // Inizializza il tracker una sola volta (o quando cambia lo stato auth).
  // Usa il singleton — nessun canale duplicato.
  useEffect(() => {
    if (typeof window === 'undefined' || isBot()) return;

    const visitorId = isAuthenticated && user?.id ? user.id : getOrCreateAnonymousId();
    visitorIdRef.current = visitorId;

    const { channel, ready } = getPresenceChannel();

    if (!channel) return;

    ready.then(() => {
      channel.track({
        visitor_id: visitorId,
        user_id: isAuthenticated && user?.id ? user.id : null,
        user_role: isAuthenticated && user ? (user.role || null) : null,
        current_page: location.pathname,
        joined_at: new Date().toISOString(),
      } satisfies VisitorPresence);
    });

    // Il canale singleton NON viene distrutto qui per evitare di rompere
    // l'observer (useOnlineVisitors). Viene distrutto solo all'unmount root.
    return () => {
      // Nessun cleanup locale — il singleton persiste
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]);

  // Aggiorna la pagina corrente ad ogni navigazione (senza ricreare il canale)
  useEffect(() => {
    const visitorId = visitorIdRef.current;
    if (!visitorId || isBot()) return;

    const { channel, ready } = getPresenceChannel();
    if (!channel) return;

    ready.then(() => {
      channel.track({
        visitor_id: visitorId,
        user_id: isAuthenticated && user?.id ? user.id : null,
        user_role: isAuthenticated && user ? (user.role || null) : null,
        current_page: location.pathname,
        joined_at: new Date().toISOString(),
      } satisfies VisitorPresence);
    });
  }, [location.pathname, isAuthenticated, user]);

  // Cleanup finale all'unmount dell'app root
  useEffect(() => {
    return () => {
      destroyPresenceChannel();
    };
  }, []);
}
